import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import Slider from "react-slick";
import Loader from "../../Loader";
import useSWRImmutable from "swr/immutable";
import { NextArrow, PrevArrow } from "../../ArrowCarousel";
import { ConvertDateToTimeSince } from "../../../helpers/util";
import SpotifyIcon from "../../../../public/images/icons/spotify.svg";
import AppleIcon from "../../../../public/images/icons/apple.svg";
import GoogleIcon from "../../../../public/images/icons/google.svg";
import styles from "../../../styles/multimedia/Multimedia.module.scss";

const cfUrl = process.env.API_CF_URL;
const Podcast = () => {
  const channels = {
    campaignConvos: "8188fa8c",
    newsbreakBeyondTheStories: "3b95fb24",
    lawOfDuterteLand: "4f4a99cc",
    seatOfPower: "4f4d4ba4",
  };

  const [currentChannel, setCurrentChannel] = useState(channels.campaignConvos);

  const RenderPodcastFeed = ({ podcastId }) => {
    // const fetcher = (...args) => fetch(...args).then((res) => res.json());
    const fetcher = (url, body) =>
      fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body,
      }).then(async (res) => {
        return res.json();
      });

    const { data: rawData, error } = useSWRImmutable(
      [
        `${cfUrl}/getRSS`,
        JSON.stringify({
          data: {
            type: "podcast",
            podcastId:
              podcastId === "all" ? Object.values(channels).join() : podcastId,
          },
        }),
      ],
      fetcher,
      {
        refreshInterval: 0,
        shouldRetryOnError: true,
        errorRetryInterval: 1500,
        errorRetryCount: 1,
      }
    );
    const data = rawData?.result;

    if (!error && !data) return <Loader />;
    if (error) return <p>Something went wrong, Please try again later!</p>;

    return data.map((podcastData, i) => (
      <article key={i} className={styles.articleWrapper}>
        <div className={styles.gradientBG}>
          <Link href={podcastData.link} passHref>
            <a className={styles.articlesContent} target="_blank">
              <h3>{podcastData.title}</h3>
              {podcastData.title.length + podcastData.content.length > 200 ? (
                <p
                  dangerouslySetInnerHTML={{
                    __html: podcastData.content.slice(0, 60) + "...",
                  }}
                />
              ) : (
                <p dangerouslySetInnerHTML={{ __html: podcastData.content }} />
              )}
              <p className={styles.pubDate}>
                {ConvertDateToTimeSince(podcastData.pubDate)}
              </p>
            </a>
          </Link>
        </div>
        <div className={styles.imageBGWrapper}>
          <Image
            src={podcastData.itunes.image}
            alt={podcastData.title}
            layout="fill"
            objectFit="cover"
          />
        </div>
      </article>
    ));
  };

  const loader = ({ src, width, quality }) => {
    return `${src}?w=${width}&q=${quality || 75}`;
  };

  const PodcastContent = () => {
    switch (currentChannel) {
      case channels.campaignConvos:
        return (
          <>
            <h2 className={styles.channelTitle}>Campaign Convos</h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                Campaign Convos is hosted by Rappler’s campaign reporters
                covering the top presidential and VP tandems. Every week, we
                give you kwentuhan, analysis, and inside information, straight
                from both the physical and virtual campaign trail. Through our
                conversations – sometimes irreverent but always relevant – we
                hope we help you make good choices on May 9, 2022.
              </p>

              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>
                  Subscribe & listen everywhere :
                </h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://open.spotify.com/show/7y2kkJVnEy7MLetOzlzABG"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={SpotifyIcon}
                          alt="Spotify"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://podcasts.apple.com/us/podcast/campaign-convos/id1608484763"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={AppleIcon}
                          alt="Apple Podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://anchor.fm/campaign-convos/episodes/Episode-5-Leni--Sara--and-the-women-in-the-2022-race-e1f7qmo"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={GoogleIcon}
                          alt="Google podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.newsbreakBeyondTheStories:
        return (
          <>
            <h2 className={styles.channelTitle}>
              Newsbreak Beyond The Stories
            </h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                Make sense of people, events, and controversies in the
                Philippines. Researcher-writer Jodesz Gavilan sits down with
                Rappler reporters to dissect and analyze issues.
              </p>

              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>
                  Subscribe & listen everywhere :
                </h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://open.spotify.com/show/20Vg07BLLyIISwqJhbAqp5"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={SpotifyIcon}
                          alt="Spotify"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://podcasts.apple.com/ph/podcast/newsbreak-beyond-the-stories-hosted-by-jodesz-gavilan/id964752386"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={AppleIcon}
                          alt="Apple Podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://anchor.fm/newsbreakph/episodes/Malaking-problema-ang-puro-replay-na-episodes-ng-DepEd-TV-e1erlja"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={GoogleIcon}
                          alt="Google podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.lawOfDuterteLand:
        return (
          <>
            <h2 className={styles.channelTitle}> Law of Duterte Land </h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                Let’s unpack the pressing legal issues in Rodrigo Duterte’s
                Philippines. Hosted by justice beat reporter Lian Buan.
              </p>

              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>
                  Subscribe & listen everywhere :
                </h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://open.spotify.com/show/5BKGT4WzBgF2OfWzkjMnYe"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={SpotifyIcon}
                          alt="Spotify"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://podcasts.apple.com/us/podcast/law-of-duterte-land-with-lian-buan/id1503855454?uo=4"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={AppleIcon}
                          alt="Apple Podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://anchor.fm/lawofduterteland/episodes/Episode-43-Human-rights-as-an-election-agenda-e191mj3"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={GoogleIcon}
                          alt="Google podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.seatOfPower:
        return (
          <>
            <h2 className={styles.channelTitle}> Seat of Power </h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                A podcast on the Philippine presidency and its impact on
                Filipinos hosted by Rappler Malacañang reporter Pia Ranada.
              </p>

              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>
                  Subscribe & listen everywhere :
                </h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://open.spotify.com/show/3dCADNyfGeiD0DTcDSgCAl"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={SpotifyIcon}
                          alt="Spotify"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://podcasts.apple.com/us/podcast/seat-of-power-political-leadership-with-pia-ranada/id1521985130?uo=4"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={AppleIcon}
                          alt="Apple Podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://anchor.fm/seatofpower/episodes/Episode-29-Dutertes-unconstitutional-memo-e18s23b"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={GoogleIcon}
                          alt="Google podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      default:
        return (
          <>
            <h2 className={styles.channelTitle}>ALL Podcast</h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}></p>

              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>
                  Subscribe & listen everywhere :
                </h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://open.spotify.com/show/3dCADNyfGeiD0DTcDSgCAl"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={SpotifyIcon}
                          alt="Spotify"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://podcasts.apple.com/us/podcast/seat-of-power-political-leadership-with-pia-ranada/id1521985130?uo=4"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={AppleIcon}
                          alt="Apple Podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>

                  <div className={styles.channelIcon}>
                    <Link
                      href="https://anchor.fm/seatofpower/episodes/Episode-29-Dutertes-unconstitutional-memo-e18s23b"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={GoogleIcon}
                          alt="Google podcasts"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
    }
  };

  const settings = {
    slidesToShow: 4.3,
    slidesToScroll: 3,
    arrows: true,
    dots: false,
    infinite: true,
    speed: 500,
    adaptiveHeight: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 600,
        settings: {
          centerMode: true,
          variableWidth: true,
          centerPadding: "20px",
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2.3,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const activeChannel = (selectedChannel) =>
    currentChannel === selectedChannel ? styles.active : "";

  return (
    <section className={`${styles.multimediaWrapper} ${styles.podcast}`}>
      <h2 className={styles.sectionTitle}>Podcasts</h2>
      <div className={styles.channelTabs}>
        <button
          className={activeChannel("all")}
          onClick={() => setCurrentChannel("all")}
        >
          All
        </button>
        <button
          className={activeChannel(channels.campaignConvos)}
          onClick={() => {
            setCurrentChannel(channels.campaignConvos);
          }}
        >
          Campaign Convos
        </button>
        <button
          className={activeChannel(channels.newsbreakBeyondTheStories)}
          onClick={() => setCurrentChannel(channels.newsbreakBeyondTheStories)}
        >
          Newsbreak beyond the stories
        </button>
        <button
          className={activeChannel(channels.lawOfDuterteLand)}
          onClick={() => setCurrentChannel(channels.lawOfDuterteLand)}
        >
          Law of Duterte Land
        </button>
        <button
          className={activeChannel(channels.seatOfPower)}
          onClick={() => setCurrentChannel(channels.seatOfPower)}
        >
          Seat of power
        </button>
      </div>
      <div className={styles.channelsDescription}>{PodcastContent()}</div>
      <Slider key={Date.now()} {...settings}>
        {RenderPodcastFeed({ podcastId: currentChannel })}
      </Slider>
    </section>
  );
};

export default Podcast;
